







































































































































































































































































































































































































import moment from 'moment-timezone';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import draggable from 'vuedraggable';

import { BaseVue } from '@/BaseVue';

import UiButton from '../ui/UiButton.vue';
import UiDatePicker2 from '../ui/UiDatePicker2.vue';
import UiSelect2 from '../ui/UiSelect2.vue';
import UiTextEdit from '../ui/UiTextEdit.vue';
import UiTooltip from '../ui/UiTooltip.vue';

// Define interfaces for frontend rule and server rule
interface FrontendRule {
  id: string;
  name: string;
  cadence: string;
  priority: number;
  characteristic: string;
  typeLabel: string;
  handlingMethod: string;
  startDate: Date | null;
  endDate: Date | null;
  metadata: Array<{ key: string; value: any }>;
  involvedAccounts: string;
  counterParties: string;
  counterPartyExcludeFees: boolean;
  separateByInvolvedAccounts: boolean;
  separateByCounterParty: boolean;
  separateByTrade: string;
  roundPeriod: string;
}

interface ServerRule {
  ruleName: string;
  classification: string;
  fingerPrint: string;
  rollupAction: string;
  metadata?: Record<string, any>;
  involvedAccounts?: string[];
  counterParties?: string[];
  counterPartyExcludeFees?: boolean;
  startSec?: number;
  endSec?: number;
  cadence?: string;
  _id?: string;
  priority?: number;
  separateByInvolvedAccounts?: boolean;
  separateByCounterParty?: boolean;
  separateByTrade?: string;
  roundPeriod?: string;
}

@Component({
  components: {
    UiButton,
    UiTextEdit,
    UiSelect2,
    UiDatePicker2,
    UiTooltip,
    draggable,
  },
})
export default class BabelRollupConfig extends BaseVue {
  @Prop({ default: () => [] })
  public readonly initialRules!: any[];

  @Prop({ default: () => false })
  public readonly editable!: boolean;

  // Computed property to check if form is valid
  get isFormValid() {
    // Check required fields
    const requiredFieldsValid =
      this.newRule.name.trim() !== '' &&
      this.newRule.characteristic.trim() !== '' &&
      this.newRule.typeLabel.trim() !== '' &&
      this.newRule.handlingMethod.trim() !== '';

    // Check if all metadata entries have both key and value
    const metadataValid = this.newRule.metadata.every((meta) => {
      // If there are metadata entries, ensure each one has both key and value
      return meta.key?.trim() && meta.value?.toString().trim();
    });
    return requiredFieldsValid && metadataValid;
  }

  // Component state
  private isCreatingRule = false;
  private expandAdvanced = false;
  public rules: FrontendRule[] = [];

  @Watch('initialRules', { immediate: true, deep: true })
  onInitialRulesChange() {
    // Only initialize if we have initialRules and our local rules are empty
    if (this.initialRules?.length > 0 && this.rules.length === 0) {
      // Convert each rule to frontend format if needed
      this.rules = this.initialRules.map((rule) => {
        // Check if the rule is already in frontend format
        if ('name' in rule && 'cadence' in rule) {
          return rule as FrontendRule;
        } else {
          // Convert from server format to frontend format
          return this.toFrontendFormat(rule as ServerRule);
        }
      });
    }
  }

  // Form data
  public newRule: FrontendRule = {
    id: '',
    name: '', // maps to ruleName
    cadence: 'hour', // maps to RollupPeriod enum
    priority: 1,
    characteristic: 'any', // maps to fingerPrint (FingerPrintType enum)
    typeLabel: '', // maps to classification
    handlingMethod: 'rollup', // maps to rollupAction (RollupAction enum)
    startDate: null, // maps to startSec (needs conversion from Date to seconds)
    endDate: null, // maps to endSec (needs conversion from Date to seconds)
    metadata: [], // Initialize as array
    involvedAccounts: '', // needs to be split into string[] for server
    counterParties: '', // needs to be split into string[] for server
    counterPartyExcludeFees: false,
    separateByInvolvedAccounts: false, // controls whether to roll up involved addresses separately
    separateByCounterParty: false, // controls whether to roll up counterparty addresses separately
    separateByTrade: 'assets',
    roundPeriod: 'end-of-period',
  };

  // Form options - matching backend enums
  public cadenceOptions = ['hour', 'day', 'month'];

  public separateByTradeOptions = [
    { value: 'none', label: 'None' },
    { value: 'assets', label: 'Assets' },
    { value: 'direction', label: 'Match Direction' },
  ];

  public handlingMethodOptions = ['rollup', 'rollupFromTo', 'nonRollup', 'ignore'];
  public roundPeriodOptions = ['start-of-period', 'end-of-period'];

  public ruleCharacteristicsOptions = [
    { value: 'incomingNative', label: 'Incoming Native ' }, // OnlyIncomingNative
    { value: 'incomingToken', label: 'Incoming Token ' }, // OnlyIncomingToken
    { value: 'outgoingNative', label: 'Outgoing Native ' }, // OnlyOutgoingNative
    { value: 'outgoingToken', label: 'Outgoing Token ' }, // OnlyOutgoingToken
    { value: 'onlyFee', label: 'Only Fee ' }, // OnlyFee
    { value: 'errored', label: 'Errored ' }, // Errored
    { value: 'any', label: 'All ' }, // Any
    { value: 'simpleIncoming', label: 'Simple Incoming ' }, // Any txn where the wallet is involved in 1 incoming movement + optional fee
    { value: 'simpleOutgoing', label: 'Simple Outgoing ' }, // any txn where the wallet is involved in 1 outgoing movement + optional fee
    { value: 'simpleTrade', label: 'Simple Trade ' }, // any txn that the wallet has 1 incmoing and outgoing movement of different assets
  ];

  // Define all available metadata keys
  private allMetadataKeys: { id: string; label: string; featureFlag?: string }[] = [
    { id: 'DestinationTag', label: 'Destination Tag' },
    { id: 'FeeType', label: 'Fee Type' },
    { id: 'TransactionType', label: 'Transaction Type' },
    { id: 'RewardType', label: 'Reward Type' },
    { id: 'RewardFeeType', label: 'Reward Fee Type' },
    { id: 'exampleMetadataKey', label: 'Example Metadata Key', featureFlag: 'exampleDontSet' },
    { id: 'geneva_curr', label: 'Geneva Currency' },
    { id: 'trantype', label: 'Trantype' },
    { id: 'geneva_price', label: 'Geneva Price' },
    { id: 'eventId', label: 'Event Id' },
    { id: 'custodian_source', label: 'Custodian Source' },
    { id: 'strat', label: 'FundStrategy' },
    { id: 'staking_source', label: 'Staking Source' },
    { id: 'isReward', label: 'Is Reward', featureFlag: 'inferstakingDemo' },
    { id: 'comment', label: 'Comment', featureFlag: 'inferstakingDemo' },
    { id: 'TransactionTypeLabel', label: 'Transaction Type Label' },
    { id: 'UTXO_TYPE', label: 'UTXO Type' },
  ];

  // Filter metadata keys based on feature flags
  get metadataKeys() {
    return this.allMetadataKeys.filter((key) => {
      // Include keys with no feature flag or where the feature flag is enabled
      return !key.featureFlag || this.checkFeatureFlag(key.featureFlag);
    });
  }

  // Get currently used metadata keys in the rule
  get usedMetadataKeys(): string[] {
    if (!this.newRule.metadata || this.newRule.metadata.length === 0) return [];
    return this.newRule.metadata
      .filter((meta) => meta.key) // Only include non-empty keys
      .map((meta) => meta.key);
  }

  // Check if a specific key is the current metadata key for a specific index
  isCurrentMetadataKey(key: string, index: number): boolean {
    return this.newRule.metadata[index].key === key;
  }

  // Get available metadata keys for a specific metadata entry
  availableMetadataKeys(currentIndex: number) {
    return this.metadataKeys.filter((key) => {
      // If the key has no feature flag or is enabled
      const isAvailable = !key.featureFlag || this.checkFeatureFlag(key.featureFlag);

      if (!isAvailable) return false;

      // If the key is already used in another metadata entry, filter it out
      const isUsed = this.usedMetadataKeys.includes(key.id);

      // If it's used, check if it's used by the current index (if so, keep it in the list)
      if (isUsed) {
        return this.isCurrentMetadataKey(key.id, currentIndex);
      }

      // Otherwise include it in the options
      return true;
    });
  }

  // Type label validation regex pattern - only allow underscores, dashes, and alphanumeric characters
  private typeLabelPattern = /^[a-zA-Z0-9_-]+$/;

  // Validate the typeLabel field
  get isTypeLabelValid() {
    const typeLabel = this.newRule.typeLabel;
    return !!typeLabel && typeLabel.trim().length > 0 && this.typeLabelPattern.test(typeLabel);
  }

  // Provide appropriate validation message for typeLabel
  get typeLabelValidationMessage() {
    const typeLabel = this.newRule.typeLabel;
    if (!typeLabel || typeLabel.trim().length === 0) {
      return 'Transaction Type Label is required';
    }
    if (!this.typeLabelPattern.test(typeLabel)) {
      return 'Only underscores, dashes, and alphanumeric characters allowed (no spaces)';
    }
    return '';
  }

  // Methods
  public startCreatingRule() {
    this.isCreatingRule = true;
  }

  public cancelRuleCreation() {
    this.isCreatingRule = false;
    this.resetNewRule();
  }

  private editingRuleIndex: number | null = null;

  get orgTimezone() {
    return this.$store.state.currentOrg.timezone || moment.tz.guess();
  }

  // Convert frontend rule to backend DTO format
  private toServerFormat(rule: FrontendRule): ServerRule {
    // When user selects dates, treat them as if they're in org timezone
    let startSec: number | undefined;
    if (rule.startDate) {
      // Extract year, month, day from the Date object
      const year = rule.startDate.getFullYear();
      const month = rule.startDate.getMonth();
      const day = rule.startDate.getDate();

      // Create a moment in the org timezone with these values
      const startDateInOrgTz = moment.tz([year, month, day], this.orgTimezone).startOf('day');
      startSec = Math.floor(startDateInOrgTz.valueOf() / 1000);
    }

    let endSec: number | undefined;
    if (rule.endDate) {
      // Extract year, month, day from the Date object
      const year = rule.endDate.getFullYear();
      const month = rule.endDate.getMonth();
      const day = rule.endDate.getDate();

      // Create a moment in the org timezone with these values
      const endDateInOrgTz = moment.tz([year, month, day], this.orgTimezone).endOf('day');
      endSec = Math.floor(endDateInOrgTz.valueOf() / 1000);
    }

    // Convert involved accounts string to array if present
    const involvedAccounts = rule.involvedAccounts
      ? rule.involvedAccounts
          .split(',')
          .map((account: string) => account.trim())
          .filter((account: string) => account !== '')
      : undefined;

    // Convert counter parties string to array if present
    const counterParties = rule.counterParties
      ? rule.counterParties
          .split(',')
          .map((party: string) => party.trim())
          .filter((party: string) => party !== '')
      : undefined;

    // Convert metadata to Record<string, string> if present
    const metadata =
      rule.metadata && rule.metadata.length > 0
        ? rule.metadata.reduce((acc: Record<string, string>, item: any) => {
            if (item.key && item.value) acc[item.key] = item.value;
            return acc;
          }, {})
        : undefined;

    return {
      ruleName: rule.name,
      classification: rule.typeLabel,
      fingerPrint: rule.characteristic,
      rollupAction: rule.handlingMethod,
      cadence: rule.cadence,
      metadata,
      involvedAccounts,
      counterParties,
      counterPartyExcludeFees: rule.counterPartyExcludeFees || undefined,
      startSec,
      endSec,
      // Include internal fields for frontend use
      _id: rule.id,
      priority: rule.priority,
      separateByInvolvedAccounts: rule.separateByInvolvedAccounts,
      separateByCounterParty: rule.separateByCounterParty,
      separateByTrade: rule.separateByTrade,
      roundPeriod: rule.roundPeriod,
    };
  }

  // Convert backend DTO to frontend format
  private toFrontendFormat(serverRule: ServerRule): FrontendRule {
    // Log the incoming server rule timestamps
    console.log('Server rule timestamps:', {
      ruleName: serverRule.ruleName,
      startSec: serverRule.startSec,
      endSec: serverRule.endSec,
    });

    // For Unix timestamps from the server, maintain the date as it appears in org timezone
    // This way when user sees 1/1/2020, it's 1/1/2020 in org timezone
    let startDate = null;
    if (serverRule.startSec) {
      // Create date as it appears in org timezone
      // We use UTC methods to avoid timezone conversion during date creation
      const orgMoment = moment.unix(serverRule.startSec).tz(this.orgTimezone);
      const year = orgMoment.year();
      const month = orgMoment.month(); // 0-11
      const day = orgMoment.date();

      // Create the date as if it were in local timezone
      startDate = new Date(year, month, day);
    }

    let endDate = null;
    if (serverRule.endSec) {
      // Create date as it appears in org timezone
      // We use UTC methods to avoid timezone conversion during date creation
      const orgMoment = moment.unix(serverRule.endSec).tz(this.orgTimezone);
      const year = orgMoment.year();
      const month = orgMoment.month(); // 0-11
      const day = orgMoment.date();

      // Create the date as if it were in local timezone
      endDate = new Date(year, month, day);
    }

    // Log the converted Date objects
    console.log('Created dates to match org timezone display:', {
      startDate,
      endDate,
      orgTimezone: this.orgTimezone,
      startDateISO: startDate?.toISOString(),
      endDateISO: endDate?.toISOString(),
    });

    return {
      id: serverRule._id || `rule-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      name: serverRule.ruleName,
      cadence: serverRule.cadence || 'hour',
      priority: serverRule.priority || this.rules.length + 1,
      characteristic: serverRule.fingerPrint,
      typeLabel: serverRule.classification,
      handlingMethod: serverRule.rollupAction,
      startDate,
      endDate,
      metadata: serverRule.metadata ? Object.entries(serverRule.metadata).map(([key, value]) => ({ key, value })) : [],
      involvedAccounts: serverRule.involvedAccounts ? serverRule.involvedAccounts.join(', ') : '',
      counterParties: serverRule.counterParties ? serverRule.counterParties.join(', ') : '',
      counterPartyExcludeFees: serverRule.counterPartyExcludeFees || false,
      separateByInvolvedAccounts: serverRule.separateByInvolvedAccounts || false,
      separateByCounterParty: serverRule.separateByCounterParty || false,
      separateByTrade: serverRule.separateByTrade ?? 'none',
      roundPeriod: serverRule.roundPeriod ?? 'end-of-period',
    };
  }

  public saveRule() {
    if (this.editingRuleIndex !== null) {
      // Update existing rule
      this.rules[this.editingRuleIndex] = { ...this.newRule };
      this.editingRuleIndex = null;
    } else {
      // Add new rule with unique ID
      const newRule = {
        ...this.newRule,
        id: `rule-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
        priority: this.rules.length + 1, // Set priority based on current rules length
      };
      this.rules.push(newRule);
    }

    this.isCreatingRule = false;
    this.resetNewRule();

    // Convert rules to server format before emitting
    const serverRules = this.rules.map((rule) => this.toServerFormat(rule));

    // Emit the updated rules to parent component
    this.$emit('update:rules', serverRules);
  }

  public editRule(rule: any) {
    const index = this.rules.indexOf(rule);
    if (index !== -1) {
      this.editingRuleIndex = index;

      // If the rule is in server format, convert it to frontend format
      if (rule.ruleName) {
        this.newRule = this.toFrontendFormat(rule);
      } else {
        // Otherwise, it's already in frontend format
        this.newRule = { ...rule };
      }

      this.isCreatingRule = true;
    }
  }

  public deleteRule(rule: any) {
    const index = this.rules.indexOf(rule);
    if (index !== -1) {
      this.rules.splice(index, 1);

      // Convert rules to server format before emitting
      const serverRules = this.rules.map((rule) => this.toServerFormat(rule));
      // Emit the updated rules to parent
      this.$emit('update:rules', serverRules);
    }
  }

  public onRulesReordered() {
    // When rules are reordered, update their priorities and emit to parent
    this.rules.forEach((rule, index) => {
      rule.priority = index + 1;
    });
    this.$emit('update:rules', [...this.rules]);
  }

  public addMetadata() {
    console.log('Adding metadata');
    if (!this.newRule.metadata) {
      this.newRule.metadata = [];
    }
    this.newRule.metadata.push({ key: '', value: '' });
  }

  public removeMetadata(index: number) {
    this.newRule.metadata.splice(index, 1);
  }

  public onSeparateByInvolvedAccountsChange() {
    if (this.newRule.separateByInvolvedAccounts) {
      this.newRule.separateByCounterParty = false;
    }
  }

  public onSeparateByCounterPartyChange() {
    if (this.newRule.separateByCounterParty) {
      this.newRule.separateByInvolvedAccounts = false;
    }
  }

  public resetCharacteristics() {
    this.newRule.separateByTrade = 'assets';
    this.newRule.separateByCounterParty = false;
    this.newRule.separateByInvolvedAccounts = false;
  }

  public separateByTradeLabel(rule: any) {
    return this.separateByTradeOptions.find((item) => item.value === rule.separateByTrade)?.label;
  }

  private resetNewRule() {
    this.newRule = {
      id: '',
      name: '',
      cadence: 'hour',
      characteristic: 'any',
      priority: 1,
      typeLabel: '',
      handlingMethod: 'rollup',
      startDate: null,
      endDate: null,
      metadata: [], // Initialize as array
      involvedAccounts: '',
      counterParties: '',
      counterPartyExcludeFees: false,
      separateByInvolvedAccounts: false,
      separateByCounterParty: false,
      separateByTrade: 'assets',
      roundPeriod: 'end-of-period',
    };
  }
}
